import React from "react";
import PropTypes from "prop-types";
import {
  DatePicker,
  Checkbox,
  Flex,
  Box,
  Text,
  Divider,
  Dropdown,
} from "samba-ui";
import moment from "moment-timezone";
import {
  StyledMainContainer,
  DateContainer,
  LabelSubText,
  CriteriaInfo,
  CriteriaContainer,
  Criteria,
  Dates,
} from "./styles";
import {
  getCriteriaByFeedType,
  getFeedMinAndMaxDates,
  getMinDateWithReportConfig,
} from "../../../../utils";
import { InputContainer } from "../ClientAndType/styles";
import {
  minStarDateForGeoWeights,
  minStartDateForXref,
  minStartDateForTivoXref,
  buyOrContentTypeFeeds,
  xRefCadenceOptions,
  buyOrContentTypeFeedsOptions,
} from "../../../../constants";
import { Label, HelperText, RadioWrapper } from "../styles";

const ADHOC_MONTH_RANGE = 1;

const PBSE132142END_DATE = "2022-06-30";

const cadenceOptions = [
  { label: "Weekly", value: "weekly" },
  { label: "28 Days", value: "28 days" },
];

const xRefCadences = [
  { label: "Daily", value: "daily" },
  { label: "Weekday", value: "weekday" },
];


function getThirdPartyOptions (feedType, version) {

  const isV2 = version === "2.0.0" || version === "2.1.0"
  const isContent = feedType === "all viewership content" || feedType === "tivo all viewership content"
  const isViewershipOrAd = feedType === "all viewership" || feedType === "all viewership ad"
    || feedType === "tivo all viewership" || feedType === "tivo all viewership ad"

  if (isContent && isV2) {
    return [
      {
        label: "IMDB ID",
        value: "imdb",
      }
    ];
  }

  if (isV2 && isViewershipOrAd) {
    return [
      {
        label: "Kinetiq ID",
        value: "kinetiq",
      },
      {
        label: "Admo ID",
        value: "admo",
      }
    ];
  }

  return [];
}


class DatesAndCriteria extends React.Component {
  componentDidMount() {
    this.props.setInitialSelectedValues();
    const { feed } = this.props;
    if (
      feed.country.value === "US" &&
      feed.pbse_version &&
      feed.pbse_version.value === "1.4.2"
    ) {
      this.onChooseDate("service_stop", moment(PBSE132142END_DATE));
    }

    if (feed.type === "geo weights") {
      this.props.onCheckAllChange(undefined, true);
    }

    if (feed.type === "spot exposure") {
      this.props.onCheckAllChange(undefined, true,
        ['tv_input_type', 'source_device_category', 'source_device', 'feed_id', 'intermediate_id']);
    }

    if (!feed.service_start) {
      this.onChooseDate("service_start", moment());
    }
  }

  onChangeThirdParty = (third_party_id) => {
    const { feed } = this.props;
    const nextMiscJson = feed.misc_json ? {  ...feed.misc_json } : {}

    if (third_party_id) {
      nextMiscJson.third_party_id = third_party_id;
    } else {
      delete nextMiscJson.third_party_id
    }

    this.props.onChangeFeed("misc_json", nextMiscJson);

  }

  onChooseDate = (type, date) => {
    if (!moment.isMoment(date) || !date.isValid()) {
      date = null;
    }

    this.props.onChangeFeed(type, date);
  };

  getConstraints = () => {
    let { feed, isSample } = this.props;

    let endMaxDate = null;
    // For Adhoc only allowed to select 1 month date range
    if (feed.service_start) {
      endMaxDate = isSample
        ? moment(feed.service_start).add(ADHOC_MONTH_RANGE, "month")
        : null;
    }

    let { minStartDate, minEndDate } = getFeedMinAndMaxDates(feed);

    if (feed.type === "geo weights") {
      minStartDate = moment(minStarDateForGeoWeights);
    }
    if (feed.type === "x_ref") {
      minStartDate = moment(minStartDateForXref);
    }
    if (feed.type === "tivo x_ref" || feed.type === "tivo+acr x_ref") {
      minStartDate = moment(minStartDateForTivoXref);
    }

    // Use the configs that come from the server to determine the min date
    // if there is a config for the feed type
    const configTypes = new Set()

    this.props.reportConfigs.forEach((config) => {
      configTypes.add(config.type)
    })

    if (configTypes.has(feed.type)) {
      const { error: configError, minDate: configMinDate } = getMinDateWithReportConfig(feed, this.props.reportConfigs)
      if (configError) {
        return (
        <div 
          style={{
            background: '#f44336',
            padding: '10px'
          }}>
            {configError}
          </div>
        )
      }

      minStartDate = moment(configMinDate)
    }

    if (feed.pbse_version && feed.pbse_version.value === "1.4.2") {
      if (feed.country.value === "US") {
        isSample = true;
        endMaxDate = moment(PBSE132142END_DATE);
      }
    }

    return {
      minStartDate,
      endMaxDate,
      minEndDate,
      isSample,
    }
  }

  render() {

    let { feed } = this.props;
    const { country, pbse_version } = feed;

    let CRITERIAS = [...getCriteriaByFeedType(feed.type, country, pbse_version)];

    const { 
      minStartDate, 
      endMaxDate, 
      minEndDate, 
      isSample
    } = this.getConstraints()

    const isMonday = (date) => {
      return date.day() === 1;
    };

    const isTuesday = (date) => {
      return date.day() === 2;
    };
    
    const thirdPartyOptions = getThirdPartyOptions(feed.type, feed.pbse_version && feed.pbse_version.value)

    const third_party_id = feed.misc_json && feed.misc_json.third_party_id

    const thirdPartyValue = third_party_id ? {
      value: third_party_id,
      label: thirdPartyOptions.find((option) => option.value === third_party_id).label
    } : null


    return (
      <StyledMainContainer>
        <Dates>
          <DateContainer>
            <Label>Start date</Label>
            <DatePicker
              minDate={minStartDate}
              maxDate={feed.service_stop}
              selectedDate={feed.service_start}
              filterDate={
                feed.type === "demographic panel" || feed.type === "geo weights" || feed.type === 'streaming geo panel'
                  ? isMonday
                  : feed.type === "tivo x_ref" || feed.type === "tivo+acr x_ref"
                    ? isTuesday
                    : () => true
              }
              onChooseDate={(date) => {
                this.onChooseDate("service_start", date);
                setTimeout(() => {
                  this.onChooseDate("service_stop", null);
                }, 0);
              }}
            />
          </DateContainer>
          <DateContainer>
            <Label>
              End date
              {!isSample ? <LabelSubText>(optional)</LabelSubText> : null}
            </Label>
            <DatePicker
              minDate={minEndDate}
              maxDate={endMaxDate}
              selectedDate={feed.service_stop}
              onChooseDate={(date) => this.onChooseDate("service_stop", date)}
            />
          </DateContainer>
        </Dates>

        {(feed.type === "demographic panel" || feed.type === "geo weights" || feed.type === 'streaming geo panel') && (
          <Box mb={3}>
            <Box>
              <Text fontSize={1} color="secondary">
                Cadence
              </Text>
              {feed.cadenceError && (
                <HelperText color="#f94d63">
                  Start date must be Monday
                </HelperText>
              )}
            </Box>
            <Box>
              <Divider />
            </Box>
            {cadenceOptions.map((c) => {
              return (
                <RadioWrapper key={c.value}>
                  <Box
                    as="input"
                    type="radio"
                    checked={feed.cadence === c.value}
                    onChange={() => this.props.onChangeFeed("cadence", c.value)}
                  />
                  <Label>{c.label}</Label>
                </RadioWrapper>
              );
            })}
          </Box>
        )}
        {feed.type === "x_ref" ? (
          <Box mb={3}>
            <Flex alignItems="center" mb={2}>
              <Text>Mapping Id:</Text>
              <Text ml={2}>ip</Text>
            </Flex>
            <Flex alignItems="center" mb={2}>
              <Text>Lookback Window:</Text>
              <Text ml={2}>26</Text>
            </Flex>
            <Text>Cadence</Text>
            <Box mt={2}>
              {xRefCadences.map((x) => (
                <RadioWrapper key={x.label}>
                  <Box
                    as="input"
                    type="radio"
                    checked={feed.x_ref_cadence === x.value}
                    onChange={() =>
                      this.props.onChangeFeed("x_ref_cadence", x.value)
                    }
                  />
                  <Label>{x.label}</Label>
                </RadioWrapper>
              ))}
            </Box>
            {feed.x_ref_cadence === "weekday" ? (
              <Box mt={2}>
                <Dropdown
                  options={xRefCadenceOptions}
                  selected={feed.x_ref_cadence_day}
                  onSelect={(option) => {
                    this.props.onChangeFeed("x_ref_cadence_day", option);
                  }}
                />
              </Box>
            ) : null}
          </Box>
        ) : null}

        {feed.type === "tivo x_ref" || feed.type === "tivo+acr x_ref" ? (
          <Box mb={3}>
            <Flex alignItems="center" mb={2}>
              <Text>Mapping Id:</Text>
              <Text ml={2}>ip</Text>
            </Flex>
            <Flex alignItems="center" mb={2}>
              <Text>Lookback Window:</Text>
              <Text ml={2}>26</Text>
            </Flex>
            <Flex alignItems="center" mb={2}>
              <Text>Cadence</Text>
              <Text ml={2}>Weekday: Tuesday</Text>
            </Flex>
          </Box>
        ) : null}

        {Object.keys(buyOrContentTypeFeeds).indexOf(feed.type) > -1 && (
          <Box mt={3}>
            <InputContainer>
              <Label>{buyOrContentTypeFeeds[feed.type]}</Label>
              <Dropdown
                options={buyOrContentTypeFeedsOptions[feed.type]}
                selected={feed.selectedBuyOrContentType}
                onSelect={(option) => {
                  this.props.onChangeFeed("selectedBuyOrContentType", option);
                }}
              />
            </InputContainer>
          </Box>
        )}

        <CriteriaInfo disabled={feed.type === "geo weights"}>
          <Label>Criteria</Label>
          {feed.type === "geo weights" && (
            <HelperText>
              You can not select column for geo weights reports.
            </HelperText>
          )}
          {feed.type !== "geo weights" && (
            <Flex alignItems="center">
              <Text fontSize={1} color="secondary">
                Check All
              </Text>
              <Box ml={3}>
                <Checkbox
                  id="checkall"
                  checked={this.props.checkAll}
                  onChange={(event) => {
                    const optInOnly = [
                      'all viewership',
                      'all viewership content',
                      'tivo all viewership',
                      'tivo all viewership content',
                    ].includes(feed.type) ? ['original_content_id'] : []
                    this.props.onCheckAllChange(event, undefined, optInOnly)
                  }}
                />
              </Box>
            </Flex>
          )}
          <HelperText>Columns that will appear in the file.</HelperText>
          <Box>
            <Divider />
          </Box>
          <CriteriaContainer>
            {CRITERIAS.map((criteria) => (
              <Criteria key={criteria}>
                <Checkbox
                  id={criteria}
                  checked={this.props.selectedCriterias[criteria] || false}
                  onChange={() => this.props.onCriteriaChange(criteria)}
                />
                {criteria}
              </Criteria>
            ))}
          </CriteriaContainer>

          {thirdPartyOptions.length > 0 && (
            <Box mt={3}>
              <Label>Third Party Id</Label>
              <Dropdown
                isClearable={true}
                options={thirdPartyOptions}
                selected={thirdPartyValue}
                onSelect={(option) => {
                  if (option && window.confirm('Please only select this if you know what you are doing!')) {
                    this.onChangeThirdParty(option.value);           
                  } else {
                    this.onChangeThirdParty(null);
                  }
                }}
              />
            </Box>
          )}
        </CriteriaInfo>
        <CriteriaContainer>
          
        </CriteriaContainer>

      </StyledMainContainer>
    );
  }
}

DatesAndCriteria.defaultProps = {
  onCriteriaChange: () => { },
  selectedCriterias: {},
  checkAll: false,
  onCheckAllChange: () => { },
};

DatesAndCriteria.propTypes = {
  feed: PropTypes.shape({
    name: PropTypes.string,
    service_start: PropTypes.object,
    service_stop: PropTypes.object,
  }),
  reportConfigs: PropTypes.array,
  onChangeFeed: PropTypes.func,
  isSample: PropTypes.bool,
  selectedCriterias: PropTypes.object,
  onCriteriaChange: PropTypes.func,
  checkAll: PropTypes.bool,
  onCheckAllChange: PropTypes.func,
  setInitialSelectedValues: PropTypes.func,
  updateCriteria: PropTypes.func,
};

export default DatesAndCriteria;
